import { useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import { files as platformFiles } from "@/platform";
import loadStore from "@/utils/generateStore";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface FileCardProps {
  conversationId: string;
  fileId: string;
  filename: string;
  sizeBytes?: number;
  mime?: string;
  compact?: boolean;
  downloadUrl?: string;
}

const formatBytes = (bytes?: number): string => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const getExtension = (name: string): string => {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1) : "";
};

export const FileCard = ({
  conversationId,
  fileId,
  filename,
  sizeBytes,
  mime,
  compact = false,
  downloadUrl,
}: FileCardProps) => {
  const { toast } = useToast();
  const [downloading, setDownloading] = useState(false);

  const fetchFileBytes = async (): Promise<Uint8Array> => {
    const store = await loadStore();
    const token = await store.get("token");
    // trd-agent detrás del PEP del BFF (#125): SÓLO al BFF; el borde afirma el
    // contexto del token, el front no manda businessId.
    const baseUrl = `${import.meta.env.VITE_BFF_URL}/api/v1/agent`;

    // Try stable backend endpoint first (survives URL expiration)
    try {
      const response = await fetch(
        `${baseUrl}/conversations/${conversationId}/files/${fileId}`,
        {
          method: "GET",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );
      if (response.ok) {
        return new Uint8Array(await response.arrayBuffer());
      }
      console.warn(
        `Stable file endpoint returned ${response.status}, falling back to presigned URL`
      );
    } catch (err) {
      console.warn(
        "Stable file endpoint unreachable, falling back to presigned URL:",
        err
      );
    }

    // Fallback to presigned downloadUrl from tool output (10 min TTL)
    if (downloadUrl) {
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return new Uint8Array(await response.arrayBuffer());
    }

    throw new Error("No download URL available");
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const data = await fetchFileBytes();
      const ext = getExtension(filename);
      const result = await platformFiles.saveBinaryFile({
        data,
        defaultName: filename,
        mimeType: mime,
        filters: ext
          ? [{ name: ext.toUpperCase(), extensions: [ext] }]
          : undefined,
      });

      if (!result.ok) {
        if (!result.cancelled) {
          throw new Error(result.error);
        }
        return;
      }

      toast({
        title: "Archivo descargado",
        description: filename,
        variant: "success",
        duration: 3000,
      });
    } catch (err) {
      console.error("File download failed:", err);
      toast({
        title: "Error",
        description: "No se pudo descargar el archivo",
        variant: "error",
        duration: 4000,
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={downloading}
      className={cn(
        "flex items-center gap-3 border border-border rounded-xl bg-background-100 hover:bg-background-200 transition text-left shadow-sm",
        compact ? "px-3 py-2 max-w-xs" : "px-4 py-3 max-w-sm",
        downloading && "opacity-70 cursor-wait"
      )}
    >
      <div
        className={cn(
          "flex items-center justify-center rounded-lg bg-gradient text-white flex-shrink-0",
          compact ? "h-8 w-8" : "h-10 w-10"
        )}
      >
        <FileText className={cn(compact ? "h-4 w-4" : "h-5 w-5")} />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            "font-medium truncate",
            compact ? "text-xs" : "text-sm"
          )}
          title={filename}
        >
          {filename}
        </div>
        <div
          className={cn(
            "text-text-secondary truncate",
            compact ? "text-[10px]" : "text-xs"
          )}
        >
          {[formatBytes(sizeBytes), mime].filter(Boolean).join(" · ")}
        </div>
      </div>
      {downloading ? (
        <Loader2
          className={cn(
            "animate-spin text-text-secondary flex-shrink-0",
            compact ? "h-4 w-4" : "h-5 w-5"
          )}
        />
      ) : (
        <Download
          className={cn(
            "text-text-secondary flex-shrink-0",
            compact ? "h-4 w-4" : "h-5 w-5"
          )}
        />
      )}
    </button>
  );
};
